import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { ApiError, type User } from "./api.ts";

/**
 * Passkey sign-in as a small, testable controller (no React, no globals: every platform call is injected).
 *
 * Why it exists: `navigator.credentials.get()` may stay pending FOREVER. Browsers treat `publicKey.timeout` as a hint;
 * when no authenticator UI can be shown, the promise may never settle (verified in Chromium: with no UI it is still
 * pending long after the declared timeout, and while it is pending every further `get()` fails with OperationError).
 * So the "Waiting for passkey…" state is never tied to that promise alone:
 *  - one bounded local timeout covers the whole attempt; it aborts the WebAuthn request (where supported) and ALWAYS
 *    ends the wait;
 *  - the user can cancel at any time;
 *  - only one attempt runs at a time (a second click gets the running attempt, never a second WebAuthn request);
 *  - challenge options are prefetched so `get()` runs directly inside the click (user-activation rules, e.g. Safari);
 *  - every failure maps to a short, non-sensitive message and leaves the button usable. Nothing retries by itself.
 */

export type PasskeyPhase = "idle" | "preparing" | "waiting" | "verifying";

export type PasskeyFailureKind =
  | "cancelled"
  | "no_credential"
  | "unavailable"
  | "not_recognized"
  | "timeout"
  | "unsupported"
  | "pending_elsewhere"
  | "expired"
  | "rate_limited"
  | "network"
  | "server";

export interface PasskeyFailure {
  kind: PasskeyFailureKind;
  message: string;
}

export type PasskeyResult = { ok: true; user: User } | { ok: false; failure: PasskeyFailure };

export const PASSKEY_MESSAGES: Record<PasskeyFailureKind, string> = {
  cancelled: "Passkey sign-in was cancelled. You can try again.",
  no_credential:
    "No passkey was selected. Try again, or use a passkey from another device if your browser offers that option.",
  unavailable:
    "No racler.dev passkey is available on this device. Try again and choose a passkey from another device, such as your phone, if your browser offers that option.",
  not_recognized: "This passkey is not registered for a racler.dev account. Try again with another passkey.",
  timeout:
    "The passkey request timed out. Try again. If no passkey prompt appears, look for a browser window behind this one.",
  unsupported:
    "This browser does not support passkey sign-in. Use a current version of Safari, Chrome, Edge or Firefox.",
  pending_elsewhere: "Another passkey request is still open in this browser. Close it, then try again.",
  expired: "The sign-in request expired. Please try again.",
  rate_limited: "Too many sign-in attempts. Wait a minute, then try again.",
  network: "Could not reach racler.dev. Check your connection and try again.",
  server: "racler.dev could not complete the sign-in. Please try again.",
};

/** Upper bound for one attempt in the UI. Matches the `timeout` the server puts into the WebAuthn options. */
export const PASSKEY_UI_TIMEOUT_MS = 120_000;
/** Prefetched options are used only while clearly inside the server's 5-minute ceremony lifetime. */
export const PASSKEY_OPTIONS_MAX_AGE_MS = 4 * 60 * 1000;
/**
 * A NotAllowedError faster than this cannot have involved a person (no prompt was shown and dismissed): the browser
 * had nothing to offer. Heuristic for the wording only; both messages point to the same remedy.
 */
export const PASSKEY_INSTANT_REJECTION_MS = 1500;

export interface LoginOptions {
  ceremonyId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

export interface PasskeyLoginDeps {
  fetchOptions(): Promise<LoginOptions>;
  startAuthentication(args: {
    optionsJSON: PublicKeyCredentialRequestOptionsJSON;
  }): Promise<AuthenticationResponseJSON>;
  verify(ceremonyId: string, credential: AuthenticationResponseJSON): Promise<User>;
  /** Aborts the pending WebAuthn request (SimpleWebAuthn: `WebAuthnAbortService.cancelCeremony`). */
  cancelCeremony(): void;
  supportsWebAuthn(): boolean;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  timeoutMs?: number;
  onPhase?: (phase: PasskeyPhase) => void;
}

type Stage = "options" | "webauthn" | "verify";

/** Internal marker for a local stop (never shown as-is). */
class LocalStop extends Error {
  readonly reason: "timeout" | "cancelled";
  constructor(reason: "timeout" | "cancelled") {
    super(reason);
    this.reason = reason;
  }
}

/** DOMException name behind an error (SimpleWebAuthn wraps them in WebAuthnError and maps aborts to a code). */
const errorName = (err: unknown): string | undefined => {
  if (!err || typeof err !== "object") return undefined;
  const e = err as { name?: unknown; code?: unknown; cause?: unknown };
  if (e.code === "ERROR_CEREMONY_ABORTED") return "AbortError";
  if (typeof e.name === "string" && e.name !== "Error" && e.name !== "WebAuthnError") return e.name;
  if (e.cause && typeof e.cause === "object") return errorName(e.cause);
  return undefined;
};

/** Maps anything thrown during a passkey sign-in to a user-facing failure. Never echoes platform or server text. */
export function classifyPasskeyError(
  err: unknown,
  ctx: { elapsedMs?: number; stage?: Stage } = {},
): PasskeyFailure {
  const fail = (kind: PasskeyFailureKind): PasskeyFailure => ({ kind, message: PASSKEY_MESSAGES[kind] });
  if (err instanceof LocalStop) {
    if (err.reason === "cancelled") return fail("cancelled");
    return fail(ctx.stage === "webauthn" ? "timeout" : "network");
  }
  if (err instanceof ApiError) {
    if (err.status === 401) return fail("not_recognized");
    if (err.code === "CEREMONY_EXPIRED") return fail("expired");
    if (err.status === 429) return fail("rate_limited");
    return fail("server");
  }
  if (err instanceof Error && /WebAuthn is not supported/i.test(err.message)) return fail("unsupported");
  if (ctx.stage !== "webauthn") return fail(err instanceof TypeError ? "network" : "server"); // fetch() -> TypeError
  switch (errorName(err)) {
    case "NotAllowedError":
      return ctx.elapsedMs !== undefined && ctx.elapsedMs < PASSKEY_INSTANT_REJECTION_MS
        ? fail("unavailable")
        : fail("no_credential");
    case "AbortError":
      return fail("cancelled");
    case "NotSupportedError":
    case "SecurityError":
      return fail("unsupported");
    case "OperationError":
      return fail("pending_elsewhere");
    case "InvalidStateError":
    case "UnknownError":
    case "ConstraintError":
      return fail("unavailable");
    default:
      return fail("no_credential");
  }
}

export interface PasskeyLogin {
  /** Starts a sign-in, or returns the attempt that is already running (never two WebAuthn requests at once). */
  signIn(): Promise<PasskeyResult>;
  /** Ends the running attempt now (aborts the WebAuthn request where supported). No-op when idle. */
  cancel(): void;
  /** Fetches challenge options in the background so the next click can call WebAuthn immediately. */
  prefetch(): void;
  readonly phase: PasskeyPhase;
}

export function createPasskeyLogin(deps: PasskeyLoginDeps): PasskeyLogin {
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const timeoutMs = deps.timeoutMs ?? PASSKEY_UI_TIMEOUT_MS;

  let phase: PasskeyPhase = "idle";
  let running: Promise<PasskeyResult> | null = null;
  let stop: ((reason: "timeout" | "cancelled") => void) | null = null;
  let prefetched: { at: number; value: Promise<LoginOptions> } | null = null;

  const setPhase = (p: PasskeyPhase) => {
    phase = p;
    deps.onPhase?.(p);
  };

  /** Single use: a challenge is never offered twice. A stale or failed prefetch falls back to a fresh request. */
  const takeOptions = (): Promise<LoginOptions> => {
    const p = prefetched;
    prefetched = null;
    if (p && now() - p.at < PASSKEY_OPTIONS_MAX_AGE_MS) return p.value.catch(() => deps.fetchOptions());
    return deps.fetchOptions();
  };

  const attempt = async (): Promise<PasskeyResult> => {
    if (!deps.supportsWebAuthn()) {
      return { ok: false, failure: classifyPasskeyError(new Error("WebAuthn is not supported")) };
    }
    let stage: Stage = "options";
    let startedAt = now();
    // Rejects on the local timeout or a cancel. Every await below races against it, so the attempt always ends,
    // even when a platform promise never settles.
    const stopped = new Promise<never>((_, reject) => {
      stop = (reason) => {
        stop = null;
        reject(new LocalStop(reason));
        if (stage === "webauthn") deps.cancelCeremony();
      };
    });
    stopped.catch(() => undefined);
    const timer = setTimer(() => stop?.("timeout"), timeoutMs);
    try {
      setPhase("preparing");
      const { ceremonyId, options } = await Promise.race([takeOptions(), stopped]);
      stage = "webauthn";
      startedAt = now();
      setPhase("waiting");
      const credential = await Promise.race([deps.startAuthentication({ optionsJSON: options }), stopped]);
      stage = "verify";
      setPhase("verifying");
      const user = await Promise.race([deps.verify(ceremonyId, credential), stopped]);
      return { ok: true, user };
    } catch (err) {
      const elapsedMs = stage === "webauthn" ? now() - startedAt : undefined;
      return { ok: false, failure: classifyPasskeyError(err, { stage, elapsedMs }) };
    } finally {
      clearTimer(timer);
      stop = null;
      setPhase("idle");
    }
  };

  return {
    get phase() {
      return phase;
    },
    signIn() {
      if (running) return running;
      running = attempt().finally(() => {
        running = null;
      });
      return running;
    },
    cancel() {
      stop?.("cancelled");
    },
    prefetch() {
      if (running || prefetched) return;
      const value = deps.fetchOptions();
      value.catch(() => undefined); // a failed prefetch only means the click fetches by itself
      prefetched = { at: now(), value };
    },
  };
}
